import { useEffect, useState, useRef } from 'react';

interface UsePosterFallbackOptions {
  enabled?: boolean;
  fallbackPoster?: string;
  showLoadingOverlay?: boolean;
}

export const usePosterFallback = (
  playerRef: React.RefObject<any>,
  options: UsePosterFallbackOptions = {}
) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showOverlay, setShowOverlay] = useState(true);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
  const { enabled = true, fallbackPoster, showLoadingOverlay = true } = options;

  useEffect(() => {
    if (!playerRef.current || !enabled) {
      return;
    }

    const handleLoadStart = () => {
      setIsLoading(true);
      setShowOverlay(true);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      // Keep overlay visible until user interaction or autoplay starts
      if (!hasStartedPlaying) {
        setShowOverlay(true);
      }
    };

    const handlePlay = () => {
      setHasStartedPlaying(true);
      setShowOverlay(false);
    };

    const handleLoadedMetadata = () => {
      setIsLoading(false);
    };

    // Add event listeners
    const video = playerRef.current;
    if (video) {
      video.addEventListener('loadstart', handleLoadStart);
      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('play', handlePlay);
      video.addEventListener('loadedmetadata', handleLoadedMetadata);

      return () => {
        video.removeEventListener('loadstart', handleLoadStart);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [playerRef, enabled, hasStartedPlaying]);

  const hideOverlay = () => {
    setShowOverlay(false);
  };

  return {
    isLoading,
    showOverlay: showLoadingOverlay && showOverlay,
    hideOverlay,
    hasStartedPlaying
  };
}; 